import { useCallback, useEffect, useState } from 'react';
import { useSessionContext } from '../contexts/SessionContext';
import type { ServerMessage } from '@roll1/shared';

/**
 * useUserUiPrefs — cross-device persistent UI state, server-backed
 * via `User.uiPreferences` JSONB column (Feature B-03).
 *
 * Replaces `localStorage` for HUD UI state (mode, position, last tab,
 * panel collapsed flags, etc.) so the user's choices follow them across
 * browsers / devices.
 *
 * Pattern:
 *   - On first mount per session: send `user:ui-prefs-get`, wait for
 *     `user:ui-prefs-updated` reply, store в local React state.
 *   - On `setPref` / `setPrefs`: optimistically update local state,
 *     dispatch `user:ui-prefs-set` к server, accept server's echoed
 *     `user:ui-prefs-updated` as authoritative (overwrites local).
 *   - One-time migration: if server prefs are empty AND localStorage has
 *     legacy HUD_UI_STORAGE_KEY content, upload it before clearing the
 *     local key. Gated by а sessionStorage flag к avoid retry on remount.
 *
 * Anonymous users (not logged in) skip server I/O entirely и fall back
 * к pure localStorage — the server has no User row к persist into.
 */

const LEGACY_LOCAL_KEY = 'roll1:character-hud-ui';
const MIGRATION_FLAG_KEY = 'roll1:ui-prefs-migrated';

export interface UseUserUiPrefsReturn {
  prefs: Record<string, unknown>;
  setPref: (key: string, value: unknown) => void;
  setPrefs: (patch: Record<string, unknown>) => void;
  /**
   * True once the server's authoritative prefs have arrived (or immediately
   * for anonymous users, who have no server side). Auto-writers (effects that
   * push local state to the server without a user action, e.g. ThemeSync)
   * MUST wait for this — writing against the initial `{}` overwrites the
   * cross-device value before it is ever seen.
   */
  ready: boolean;
}

function readLegacyLocalStorage(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function clearLegacyLocalStorage(): void {
  try {
    localStorage.removeItem(LEGACY_LOCAL_KEY);
  } catch {
    /* localStorage unavailable — ignore */
  }
}

function isMigrationDone(): boolean {
  try {
    return sessionStorage.getItem(MIGRATION_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function markMigrationDone(): void {
  try {
    sessionStorage.setItem(MIGRATION_FLAG_KEY, '1');
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

export function useUserUiPrefs(): UseUserUiPrefsReturn {
  const ctx = useSessionContext() as Partial<ReturnType<typeof useSessionContext>>;
  // Tolerant of partial mocks (vi.mock в unit tests may omit `auth`).
  const mp = ctx.mp;
  const auth = ctx.auth;
  const userId = auth?.user?.id ?? null;
  const [prefs, setLocalPrefs] = useState<Record<string, unknown>>(() => {
    // Bootstrap from legacy localStorage so the first render isn't blank
    // before the server reply arrives. Server response overrides anyway.
    return readLegacyLocalStorage() ?? {};
  });
  const [serverSynced, setServerSynced] = useState(false);

  // Subscribe к server's authoritative reply. The same broadcast type also
  // delivers the result of `setPrefs` calls — both flows update local state.
  useEffect(() => {
    if (!mp?.onMessage) return;
    const off = mp.onMessage((msg: ServerMessage) => {
      if (msg.type !== 'user:ui-prefs-updated') return;
      if (userId && msg.userId !== userId) return; // ignore other users' echoes
      const next = (msg.prefs ?? {}) as Record<string, unknown>;
      setLocalPrefs(next);
      setServerSynced(true);
    });
    return off;
  }, [mp, userId]);

  // On mount (or when userId becomes available): fetch + one-time migration.
  useEffect(() => {
    if (!userId || !mp?.send) return; // anonymous OR test mock — server I/O is а no-op
    mp.send({ type: 'user:ui-prefs-get' });
  }, [mp, userId]);

  // One-time legacy → server upload after we know the server side is empty.
  useEffect(() => {
    if (!userId || !mp?.send) return;
    if (isMigrationDone()) return;
    if (Object.keys(prefs).length !== 0) return; // server already has data — no migration
    const legacy = readLegacyLocalStorage();
    if (!legacy || Object.keys(legacy).length === 0) {
      markMigrationDone();
      return;
    }
    mp.send({ type: 'user:ui-prefs-set', prefs: legacy });
    clearLegacyLocalStorage();
    markMigrationDone();
  }, [mp, userId, prefs]);

  const setPrefs = useCallback(
    (patch: Record<string, unknown>) => {
      // Optimistic local update — server echo will overwrite shortly с
      // the authoritative merge result.
      setLocalPrefs((prev) => ({ ...prev, ...patch }));
      if (userId && mp?.send) {
        mp.send({ type: 'user:ui-prefs-set', prefs: patch });
      }
    },
    [mp, userId],
  );

  const setPref = useCallback(
    (key: string, value: unknown) => {
      setPrefs({ [key]: value });
    },
    [setPrefs],
  );

  // Anonymous users have no server side to wait for.
  return { prefs, setPref, setPrefs, ready: userId === null || serverSynced };
}
